/**
 * Putting the kiosk together, without React: from the flags and a pack to the app object every
 * component reads. Also the store wiring that is not a component's business (draft per screen,
 * overlays from replies).
 */
import { report } from '@platform/core/diagnostics';
import { playsRecording, type Flags } from '@platform/core/flags';
import type { Kiosk } from '@platform/core/kiosk';
import { loadSettings } from '@platform/core/appearance';
import { detailAfterReply } from '@platform/core/detail';
import { createUiStore, onScreen } from '@platform/core/ui-state';
import { createTranslator, mergeCatalogs } from '@platform/i18n/translator';
import { PLATFORM_STRINGS } from '@platform/i18n/strings';
import { collectPresets } from '@platform/layout/presets';
import { presetsFor, startingPreset, themeById } from '@platform/themes/themes';
import { BUILTIN_MODULES } from '@platform/modules/registry';
import type { KioskPack } from '@platform/packs/contract';
import { screenContext } from '@platform/packs/screen';
import type { KioskApp } from '@platform/react/app';
import type { ReplayPlayer } from '@platform/replay/player';
import type { ReviewContext } from '@platform/review/gym-api';
import { BUILTIN_SCENES } from '@platform/shell/scenes';
import { createCueStore, cuesFromAnswers, followAvatarEvents } from '@platform/avatar/cue-feed';
import type { FaceRenderer } from '@platform/avatar/face';
import type { VoiceController } from '@platform/voice/controller';
import { widgetRegistry } from '@platform/widgets/registry';
import { EMPTY_STAGE, stageCatalogSchema, type StageCatalog } from '@platform/stage/catalog';
import STAGE from './stage.json';

/** The build's avatars and backgrounds; an unreadable catalog is said, and the stage is empty (the theme's plain one). */
export function stageCatalog(raw: unknown = STAGE): StageCatalog {
  const parsed = stageCatalogSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  report('error', 'stage', `src/app/stage.json is unreadable: ${parsed.error.issues[0]?.path.join('.')} ${parsed.error.issues[0]?.message}`);
  return EMPTY_STAGE;
}

export interface ComposeInput {
  kiosk: Kiosk;
  pack: KioskPack;
  flags: Flags;
  voice?: VoiceController | null;
  replay?: ReplayPlayer | null;
  review?: ReviewContext | null;
  avatar?: FaceRenderer | null;
}

export function composeApp({ kiosk, pack, flags, voice = null, replay = null, review = null, avatar = null }: ComposeInput): { app: KioskApp; dispose: () => void } {
  const { presets, errors } = collectPresets(pack.presets ?? []);
  for (const e of errors) report('error', 'layout', e);
  if (flags.preset && !presets.has(flags.preset)) report('warn', 'layout', `?ui=${flags.preset}: no such preset`);
  const saved = playsRecording(flags.role) ? {} : loadSettings();
  const theme = themeById(saved.theme ?? flags.theme);
  // a preset the theme was not drawn for is not taken up, saved or asked (presetsFor)
  const initialPreset = startingPreset({ url: flags.preset, saved: saved.preset, theme, packDefault: pack.defaultPreset },
    (id) => presets.has(id) && presetsFor(theme, [id]).length > 0);

  // ?bg3d=1: the low-poly avatar replaces MuseTalk behind every preset that shows the stage.
  if (flags.lowpoly) for (const [id, p] of presets) if (p.scene === 'stage') presets.set(id, { ...p, scene: 'lowpoly' });
  const ui = createUiStore({ presetId: initialPreset, modeOverride: flags.mode, settings: saved, panel: flags.dev ? 'dev' : flags.role === 'review' ? 'review' : flags.role === 'replay' ? 'replay' : flags.role === 'operator' ? 'operator' : 'none' });
  const catalogs = mergeCatalogs(PLATFORM_STRINGS, pack.strings);

  // The emoji cues: live from the backend's avatar stream, in a replay from the recorded answers.
  const cues = createCueStore();
  const offCues = playsRecording(flags.role) ? cuesFromAnswers(kiosk.stores.conversation, cues) : followAvatarEvents(cues);

  const app: KioskApp = {
    kiosk,
    ui,
    pack,
    stage: stageCatalog(),
    presets,
    catalogs,
    modules: new Map(BUILTIN_MODULES.map((m) => [m.id, m])),
    widgets: widgetRegistry(pack.widgets ?? []),
    scenes: new Map(BUILTIN_SCENES.map((s) => [s.id, s])),
    flags,
    voice,
    replay,
    review,
    avatar,
    cues,
  };

  // A new screen starts with an empty draft and no overlay. The session may already be there (the
  // kiosk started before this): take its screen now, or its first update would look like a new
  // screen and close the overlay the first answer opened.
  onScreen(ui, kiosk.stores.session.get()?.state ?? null);
  const offScreen = kiosk.stores.session.subscribe(() => onScreen(ui, kiosk.stores.session.get()?.state ?? null));

  // A reply can ask the pack to show something over the content (the station map), and opens or closes
  // the detail of a shown item (core/detail.ts).
  let seen = 0;
  const offReplies = kiosk.stores.conversation.subscribe(() => {
    const entries = kiosk.stores.conversation.get().entries;
    if (entries.length < seen) seen = 0; // cleared (reset, replay seek)
    const fresh = entries.slice(seen);
    seen = entries.length;
    for (const entry of fresh) {
      if (entry.role === 'assistant') ui.set((s) => ({ ...s, detail: detailAfterReply(entry.message, Date.now()) }));
    }
    const session = kiosk.stores.session.get();
    if (!pack.overlayFor || !session) return;
    for (const entry of fresh) {
      if (entry.role !== 'assistant') continue;
      const ctx = screenContext(session, createTranslator(catalogs, session.language), ui.get().draft);
      const call = pack.overlayFor(entry.message, ctx);
      if (!call) continue;
      const closeAfterMs = call.closeAfterMs ?? null;
      const closeAt = closeAfterMs ? Date.now() + closeAfterMs : null;
      ui.set((s) => ({ ...s, overlay: { widget: call.widget, props: call.props, turnId: entry.turnId, closeAfterMs, closeAt } }));
    }
  });

  return {
    app,
    dispose: () => {
      offScreen();
      offReplies();
      offCues();
    },
  };
}
